import { gql } from '@/utilities/gql';
import type { ResultOf } from '@/utilities/gql';
import find from 'lodash/find';
import get from 'lodash/get';
import remove from 'lodash/remove';
import map from 'lodash/map';
import filter from 'lodash/filter';
import { gqlClient } from '@/utilities/graphQLClient';
import type { QueryFunctionContext } from '@tanstack/react-query';
import type { PrimaryCountryISO } from '@/constants/countries';
import { getPath } from '@/router/utils';
import {
  ROUTE_MARKET,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
} from '@/constants/routes';
import {
  formatterFactor,
  formatterPercentage,
} from '@/pages/Market/utils/formatter';
import type { PeriodType } from '../components/Sectors/SectorPerformance';
import { MINIMUM_COMPANIES } from './fetchIndustries';
import {
  getMarketPageType,
  MarketPageType,
} from '@/pages/Market/hooks/useReduxStateMarkets';

interface BaseEntity {
  id: number;
  name: string;
  canonicalUrl: string;
  companyCount: number | null;
  level: number;
  current?: boolean;
  isChild?: boolean;
}

export interface MarketCapEntity extends BaseEntity {
  value: number | null;
  valueFormatted: string;
}

export interface PEEntity extends BaseEntity {
  // This defines absolute PE, rather than median PE
  pe: number;
  peFormatted: string;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
}

export interface Response {
  marketCap: Record<PeriodType, MarketCapEntity[]>;
  pe: PEEntity[];
}

type IndustryAverageFieldType =
  | 'pe'
  | 'net_income_growth_annual'
  | 'return_7d'
  | 'return_30d'
  | 'return_90d'
  | 'return_1yr_abs'
  | 'return_3yr_abs'
  | 'return_5yr_abs'
  | 'total_net_income_usd'
  | 'total_market_cap_usd';

interface PathRequest {
  type:
    | typeof ROUTE_MARKET
    | typeof ROUTE_MARKET_SECTOR
    | typeof ROUTE_MARKET_SECTOR_SECONDARY
    | typeof ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY;
  payload: {
    market: PrimaryCountryISO;
    sector?: string | undefined;
    secondary?: string | undefined;
    tertiary?: string | undefined;
  };
}

const INDUSTRY_AVERAGE_FRAGMENT = gql(`
  fragment IndustryAverageFields on Industry @_unmask {
    id
    name
    friendlyName
    level
    slug
    companyCount(countryIso2: $market)
    industryAverages(countryIso2: $market, fields: $fields) {
      name
      value
    }
  }
`);

const QUERY = gql(
  `
  query getIndustryAverage(
    $industryId: Float!
    $market: String!
    $fields: [String!]
    $includeChildren: Boolean!
    $includeDescendants: Boolean!
    $includeAncestors: Boolean!
    $includeMarket: Boolean!
    $includePeers: Boolean!
  ) {
    Industry(id: $industryId, countryIso2: $market) {
      ...IndustryAverageFields
      children @include(if: $includeChildren) {
        ...IndustryAverageFields
      }
      descendants(level: 3) @include(if: $includeDescendants) {
        ...IndustryAverageFields
      }
      ancestors @include(if: $includeAncestors) {
        ...IndustryAverageFields
      }
      market @include(if: $includeMarket) {
        ...IndustryAverageFields
      }
      peers @include(if: $includePeers) {
        ...IndustryAverageFields
      }
    }
  }
`,
  [INDUSTRY_AVERAGE_FRAGMENT]
);

type Result = ResultOf<typeof QUERY>;
type Industry = Result['Industry'];

const sorting =
  (sortingKey: string, direction?: 'acs' | 'desc') =>
  (
    a: Partial<MarketCapEntity | PEEntity>,
    b: Partial<MarketCapEntity | PEEntity>
  ) =>
    direction === 'acs'
      ? a[sortingKey] - b[sortingKey]
      : b[sortingKey] - a[sortingKey];

export const createPathRequest =
  (
    market: PrimaryCountryISO,
    sector: string | undefined,
    secondary: string | undefined
  ) =>
  (level: number, slug?: string): PathRequest => {
    if (!level || !slug) {
      return {
        type: ROUTE_MARKET,
        payload: {
          market,
        },
      };
    }
    const pathRequest: PathRequest = {
      type: ROUTE_MARKET_SECTOR,
      payload: {
        market,
        sector,
        secondary,
      },
    };
    if (level === 4) {
      pathRequest.type = ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY;
      pathRequest.payload.tertiary = slug;
      return pathRequest;
    }
    if (level === 3) {
      pathRequest.type = ROUTE_MARKET_SECTOR_SECONDARY;
      pathRequest.payload.secondary = slug;
      return pathRequest;
    }

    pathRequest.payload.sector = slug;
    return pathRequest;
  };

export const transformMarketCapData =
  (field: IndustryAverageFieldType) =>
  (
    industry: Industry,
    pathRequestCreator: (number, string) => PathRequest,
    current?: boolean,
    isChild?: boolean
  ): MarketCapEntity => {
    const value = get(
      find(industry.industryAverages, ['name', field]),
      'value',
      0
    );
    return {
      id: Number(industry.id),
      name: industry.friendlyName || industry.name,
      companyCount: industry.companyCount,
      level: industry.level || 0,
      canonicalUrl: getPath(pathRequestCreator(industry.level, industry.slug)),
      value,
      valueFormatted: formatterPercentage(value, 2),
      current,
      isChild,
    };
  };

const transformPEEntityData = (
  industry: Industry,
  pathRequestCreator: (number, string) => PathRequest,
  current?: boolean,
  isChild?: boolean
): PEEntity => {
  const total_market_cap_usd = get(
    find(industry.industryAverages, ['name', 'total_market_cap_usd']),
    'value',
    0
  );
  const total_net_income_usd = get(
    find(industry.industryAverages, ['name', 'total_net_income_usd']),
    'value',
    0
  );
  const absolutePe =
    total_market_cap_usd !== 0 &&
    total_market_cap_usd !== null &&
    total_net_income_usd !== null
      ? // eslint-disable-next-line
      ((total_market_cap_usd / total_net_income_usd) / 1000000)
      : 0;
  const earningsGrowth = get(
    find(industry.industryAverages, ['name', 'net_income_growth_annual']),
    'value',
    0
  );
  return {
    id: Number(industry.id),
    name: industry.friendlyName || industry.name,
    companyCount: industry.companyCount,
    level: industry.level || 0,
    canonicalUrl: getPath(pathRequestCreator(industry.level, industry.slug)),
    pe: absolutePe,
    peFormatted: formatterFactor(absolutePe),
    earningsGrowth,
    earningsGrowthFormatted: formatterPercentage(earningsGrowth, 1),
    current,
    isChild,
  };
};

export const getDefault = (): Response => ({
  marketCap: {
    '7D': [],
    '1M': [],
    '3M': [],
    '1Y': [],
    '3Y': [],
    '5Y': [],
  },
  pe: [],
});

export const hasMinimumCompanies = (data: BaseEntity): boolean => {
  if (data.companyCount === null) return false;
  return data.companyCount >= MINIMUM_COMPANIES;
};

export const INDUSTRY_PERFORMANCE_QUERY_KEY = 'market-industry-performance';

export const getIndustryPerformanceQuery = (
  market: PrimaryCountryISO,
  industryId: number,
  sector: string,
  secondary: string,
  tertiary: string
) =>
  [
    INDUSTRY_PERFORMANCE_QUERY_KEY,
    market,
    industryId,
    sector || '',
    secondary || '',
    tertiary || '',
  ] as const;

export function constructIndustryPerformanceData<EntityType extends BaseEntity>(
  industryResponse: Result['Industry'],
  pathCreator: (number, string) => PathRequest,
  marketPageType: MarketPageType,
  transformer: (
    industry: Result['Industry'],
    pathRequestCreator: (number, string) => PathRequest,
    current?: boolean,
    isChild?: boolean
  ) => EntityType,
  sortingKey = 'value'
): Array<EntityType> {
  const market =
    industryResponse.market &&
    transformer(industryResponse.market, pathCreator);
  const ancestors = map(
    filter(industryResponse.ancestors, (a) => a.level !== 2),
    (a) => transformer(a, pathCreator)
  );
  const current = transformer(
    industryResponse,
    pathCreator,
    true,
    marketPageType === MarketPageType.TERTIARY
  );
  const children = map(industryResponse.children, (a) =>
    transformer(a, pathCreator, false, true)
  );
  const descendants = map(industryResponse.descendants, (a) =>
    transformer(a, pathCreator, false, true)
  );
  const peers = map(industryResponse.peers, (a) =>
    transformer(a, pathCreator, false, true)
  );

  const sortedChildren = [...children, ...descendants, ...peers].sort(
    sorting(sortingKey)
  );
  const sortedAncestors = ancestors.sort(sorting('level', 'acs'));

  return remove(
    [market, ...sortedAncestors, current, ...sortedChildren],
    (entity) => {
      return entity && entity?.companyCount;
    }
  ) as Array<EntityType>;
}

async function fetchIndustryPerformance({
  queryKey: [_key, market, industryId, sector, secondary, tertiary],
}: QueryFunctionContext<
  ReturnType<typeof getIndustryPerformanceQuery>
>): Promise<Response | undefined> {
  try {
    const marketPageType = getMarketPageType(
      market,
      sector,
      secondary,
      tertiary
    );
    const response = await gqlClient.request(QUERY, {
      market: market.toUpperCase(),
      industryId,
      fields: [
        'pe',
        'net_income_growth_annual',
        'return_7d',
        'return_30d',
        'return_90d',
        'return_1yr_abs',
        'return_3yr_abs',
        'return_5yr_abs',
        'total_market_cap_usd',
        'total_net_income_usd',
      ],
      includeDescendants: marketPageType === MarketPageType.SECTOR,
      includeChildren:
        marketPageType === MarketPageType.MARKET ||
        marketPageType === MarketPageType.SECONDARY,
      includeAncestors: marketPageType !== MarketPageType.MARKET,
      includeMarket: marketPageType !== MarketPageType.MARKET,
      includePeers: marketPageType === MarketPageType.TERTIARY,
    });

    const { Industry } = response;
    const pathCreator = createPathRequest(market, sector, secondary);
    return {
      marketCap: {
        '7D': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_7d')
        ),
        '1M': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_30d')
        ),
        '3M': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_90d')
        ),
        '1Y': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_1yr_abs')
        ),
        '3Y': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_3yr_abs')
        ),
        '5Y': constructIndustryPerformanceData(
          Industry,
          pathCreator,
          marketPageType,
          transformMarketCapData('return_5yr_abs')
        ),
      },
      pe: constructIndustryPerformanceData(
        Industry,
        pathCreator,
        marketPageType,
        transformPEEntityData,
        'pe'
      ),
    };
  } catch (e) {
    if (e instanceof Error) {
      console.error(e.message);
      throw new Error(e.message);
    }
  }
}

export default fetchIndustryPerformance;
